import { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Button } from 'primereact/button'
import {
  compact,
  some,
  isNil,
  isNaN,
  map,
  head,
  toNumber,
  overSome,
  get,
  isEmpty,
} from 'lodash'
import moment from 'moment-taiwan'
import { Flex } from '@changingai/react-editor-common-component'

import { inputFactory, SingleInput } from '@widget'
import { toEditableValue } from '@schema'

import { contractSchema, ContractPropType } from './schema'
import { SchemaBaseEditor } from '../SchemaBaseEditor'
import { useFetchFullDoc } from '../DocHelper'

import { _queryContract } from './editor.gql'

function parseDateString(dateString) {
  const [year, month, day] = map(dateString.split(/\.|年|月|日|\//), toNumber)
  if (some([year, month, day], overSome(isNil, isNaN))) return null
  const parsedDate = moment(`${year}/${month}/${day}`, 'tYY/MM/DD')
  return parsedDate
}

function ApproveDate({ context, onChange, onDirty, onValid }) {
  const { approval_doc_no } = context
  const extractedDate = useMemo(() => {
    if (isNil(approval_doc_no)) return null
    const matchedString = compact(
      map(
        [
          ...approval_doc_no
            .replaceAll(' ', '')
            .matchAll(
              /\d{2,3}\.\d{1,2}\.\d{1,2}|\d{2,3}年\d{1,2}月\d{1,2}日|\d{2,3}\/\d{1,2}\/\d{1,2}/g,
            ),
        ],
        matched => parseDateString(head(matched)),
      ),
    )
    return isEmpty(matchedString) ? null : moment.max(matchedString)
  }, [approval_doc_no])

  const onClick = useCallback(() => {
    if (isNil(extractedDate)) throw new Error('extractedDate should not be nil')
    onChange('approval_date', extractedDate.format('YYYY/M/D'))
    onDirty('approval_date', true)
  }, [extractedDate, onChange, onDirty])

  return (
    <>
      <SingleInput
        fieldname="approval_date"
        value={toEditableValue(
          'approval_date',
          context.approval_date,
          contractSchema,
        )}
        label={get(contractSchema, 'approval_date.label')}
        onChange={onChange}
        onDirty={onDirty}
        onValid={onValid}
        currentSchema={contractSchema}
        inputAttributes={{ width: 'calc(100% - 100px)', multilines: false }}
        labelAttributes={{ width: '200px' }}
      />
      <Flex width="100px">
        <Button
          disabled={isNil(extractedDate)}
          label="擷取日期"
          className="p-button-primary"
          onClick={onClick}
        />
      </Flex>
    </>
  )
}

ApproveDate.propTypes = {
  context: ContractPropType,
  onChange: PropTypes.func.isRequired,
  onDirty: PropTypes.func.isRequired,
  onValid: PropTypes.func.isRequired,
}

export function ContractEditor(props) {
  const { doc } = props
  const { editContext, setEditContext } = useFetchFullDoc(
    doc,
    contractSchema,
    _queryContract,
  )
  const tabs = useMemo(
    () => [
      {
        caption: 'Primary',
        configs: [
          inputFactory(contractSchema, 'contract_name'),
          inputFactory(contractSchema, 'contract_type'),
          inputFactory(contractSchema, 'insurance_company'),
          {
            fieldname: 'file_url',
            widget: 'FileUploader',
            mimetype: 'application/pdf',
          },
          {
            fieldname: 'fee_url',
            widget: 'FileUploader',
            mimetype: 'application/pdf',
          },
          inputFactory(contractSchema, 'contract_code'),
          inputFactory(contractSchema, 'oldest_approval_doc_no'),
          inputFactory(contractSchema, 'approval_doc_no'),
          {
            fieldname: 'approval_date',
            widget: 'CustomizedInput',
            RenderComp: ApproveDate,
          },
          inputFactory(contractSchema, 'start_date'),
          inputFactory(contractSchema, 'guaranteed_renewal'),
        ],
      },
      {
        caption: 'Secondary',
        configs: [
          inputFactory(contractSchema, 'version'),
          inputFactory(contractSchema, 'glossary'),
          inputFactory(contractSchema, 'exception', {
            inputAttributes: {
              multilines: true,
              rows: 5,
            },
          }),
          inputFactory(contractSchema, 'exclusion', {
            inputAttributes: {
              multilines: true,
              rows: 5,
            },
          }),
        ],
      },
    ],
    [],
  )

  return (
    <SchemaBaseEditor
      {...props}
      currentSchema={contractSchema}
      tabs={tabs}
      crud={{
        create: true,
        update: true,
        delete: true,
        read: true,
        overwrite: true,
      }}
      isReady={!isNil(editContext)}
      editContext={editContext}
      setEditContext={setEditContext}
    />
  )
}

ContractEditor.propTypes = {
  doc: ContractPropType.isRequired,
}
